import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { api } from "../lib/apiClient";
import { ExpertAvatar } from "../components/ExpertCard";
import { ArrowLeft, MapPin, Clock, BadgeCheck, Briefcase } from "lucide-react";
import { RequestReviewDialog } from "../components/RequestReviewDialog";
import { toast } from "sonner";
import { useT } from "../lib/i18n";

export default function ExpertProfile() {
  const { t } = useT();
  const { slug } = useParams();
  const [expert, setExpert] = useState(null);
  const [labels, setLabels] = useState({});
  const [notFound, setNotFound] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);

  useEffect(() => {
    Promise.all([api.get(`/experts/${slug}`), api.get("/consultants/taxonomies")])
      .then(([e, tx]) => { setExpert(e.data); setLabels(tx.data.expertise_labels); })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <DashboardLayout title={t("Expert not found")}><Link to="/marketplace" className="text-[#0EA5E9] font-bold">← {t("Back to Marketplace")}</Link></DashboardLayout>;
  if (!expert) return <DashboardLayout title={t("Loading…")}><div className="text-[#94A3B8]">{t("Loading…")}</div></DashboardLayout>;

  return (
    <DashboardLayout title={t("Expert profile")}
      action={<Link to="/marketplace" className="text-sm font-bold text-[#0B1B3D] inline-flex items-center gap-2 hover:underline"><ArrowLeft className="w-4 h-4" /> {t("Marketplace")}</Link>}>
      <div className="max-w-3xl space-y-6" data-testid="expert-profile-page">
        <div className="bg-white border border-slate-200 rounded-xl p-8">
          <div className="flex items-start gap-5">
            <ExpertAvatar expert={expert} size="w-20 h-20" />
            <div className="flex-1 min-w-0">
              <h1 className="font-display text-2xl font-black text-[#0B1B3D] flex items-center gap-2">
                {expert.first_name} {expert.last_name}
                <BadgeCheck className="w-5 h-5 text-[#0EA5E9]" />
              </h1>
              <p className="text-[#475569] mt-1">{expert.headline}</p>
              <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-[#94A3B8] font-mono-kz">
                <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {expert.city ? `${expert.city}, ` : ""}{expert.country}</span>
                <span className="inline-flex items-center gap-1"><Briefcase className="w-3 h-3" /> {expert.years_experience}+ {t("yrs")}</span>
                <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {expert.turnaround}</span>
                <span className={`px-2 py-0.5 rounded-full font-bold ${expert.availability === "AVAILABLE" ? "bg-[#10B981]/10 text-[#10B981]" : "bg-[#F59E0B]/10 text-[#F59E0B]"}`}>{t(expert.availability)}</span>
              </div>
            </div>
          </div>
          {expert.bio && <p className="mt-6 text-[#475569] leading-relaxed">{expert.bio}</p>}
          {(expert.current_role || expert.current_company) && (
            <p className="mt-3 text-sm text-[#94A3B8]">{expert.current_role}{expert.current_company ? ` · ${expert.current_company}` : ""}</p>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <p className="font-mono-kz text-xs text-[#0EA5E9] tracking-widest font-bold uppercase">{t("Expertise")}</p>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {(expert.primary_expertise || []).map((e) => <span key={e} className="px-2.5 py-1 rounded-full bg-[#0B1B3D] text-white text-xs font-bold">{labels[e] || e}</span>)}
              {(expert.secondary_expertise || []).map((e) => <span key={e} className="px-2.5 py-1 rounded-full bg-[#0EA5E9]/10 text-[#0EA5E9] text-xs font-bold">{labels[e] || e}</span>)}
            </div>
            {expert.industries?.length > 0 && (<>
              <p className="font-mono-kz text-xs text-[#0EA5E9] tracking-widest font-bold uppercase mt-5">{t("Industries")}</p>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {expert.industries.map((i) => <span key={i} className="px-2.5 py-1 rounded-full bg-slate-100 text-[#475569] text-xs font-bold">{i}</span>)}
              </div>
            </>)}
            {expert.languages?.length > 0 && (<>
              <p className="font-mono-kz text-xs text-[#0EA5E9] tracking-widest font-bold uppercase mt-5">{t("Languages")}</p>
              <div className="mt-3 space-y-1">
                {expert.languages.map((l, i) => <p key={i} className="text-sm text-[#475569]"><span className="font-bold text-[#0B1B3D]">{l.language}</span> · {l.level}</p>)}
              </div>
            </>)}
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6 flex flex-col">
            <p className="font-mono-kz text-xs text-[#0EA5E9] tracking-widest font-bold uppercase">{t("Report Review service")}</p>
            <p className="mt-3 text-sm text-[#475569]">{expert.service?.description || t("Independent professional review of your Konzenzo Decision Report: assumptions challenged, additional risks, and expert recommendations.")}</p>
            <div className="mt-auto pt-5">
              <p className="font-display font-black text-3xl text-[#0B1B3D]">€{expert.service?.base_price_eur}</p>
              <p className="text-xs text-[#94A3B8] font-mono-kz mt-1">{t("per report review")} · {expert.service?.turnaround || expert.turnaround}</p>
              <button data-testid="request-review-btn" onClick={() => {
                if (expert.stripe_status === "READY") setReviewOpen(true);
                else toast.info(t("This expert is finalizing their payment setup — check back soon."));
              }}
                className="mt-4 w-full bg-[#0B1B3D] text-white hover:bg-[#1E3A8A] px-5 py-3 rounded-lg font-bold text-sm font-display">
                {t("Request Review")}
              </button>
              <p className="text-xs text-[#94A3B8] mt-2 text-center">{t("Independent professional opinion — not a Konzenzo certification.")}</p>
            </div>
          </div>
        </div>
      </div>
      <RequestReviewDialog open={reviewOpen} onClose={() => setReviewOpen(false)} expert={expert} />
    </DashboardLayout>
  );
}
